import { observer } from "mobx-react-lite";
import { useLocalization } from "@progress/kendo-react-intl";
import { useStores } from "src/stores";
import { ExcelExport, ExcelExportColumn } from '@progress/kendo-react-excel-export';
import { useEffect, useRef, useState } from "react";
import { useServices } from "src/services";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import {
  Grid,
  GridCellProps,
  GridColumn,
  GridDataStateChangeEvent,
  GridToolbar,
} from "@progress/kendo-react-grid";
import { Button, ToolbarSpacer } from "@progress/kendo-react-buttons";
import {
  DataResult,
  process,
  State,
} from "@progress/kendo-data-query";
import { QueuedCheck } from "src/utils/classes";
import { normalizeMemo } from "src/utils/normalizeMemo";
import {
  ColumnMenu,
  ColumnMenuCheckboxFilter,
} from "src/components/GridColumnMenus";
import multiColumnSort from "multi-column-sort";
import moment from 'moment-timezone';

interface ExportColumnDef {
  key: string;
  field: string;
  title: string;
}

const CheckApprovalScreen = () => {
  const localizationService = useLocalization();
  const _export = useRef<ExcelExport | null>(null);
  const excelExport = () => {
    if (_export.current !== null) {
      _export.current.save();
    }
  };

  const exportColumnDefs: ExportColumnDef[] = [
    { key: 'check_type_pretty', field: 'check_type_pretty', title: 'Check Type' },
    { key: 'check_amount', field: 'check_amount', title: 'Check Amount' },
    { key: 'event_name', field: 'event_name', title: 'Event Name' },
    { key: 'start_date', field: 'start_date', title: 'Event Date' },
    { key: 'dealer_name', field: 'dealer_name', title: 'Dealer' },
    { key: 'ffs_event_id', field: 'ffs_event_id', title: 'FFS Event ID' },
    { key: 'created_timestamp', field: 'created_timestamp', title: 'Request Date' },
    { key: 'check_approved', field: 'check_approved', title: 'Approved Date' },
    { key: 'check_payable', field: 'check_payable', title: 'Check Payable To' },
    { key: 'event_summary', field: 'event_summary', title: 'Event Summary' },
    { key: 'memo', field: 'memo', title: 'Memo' },
    { key: 'status', field: 'status', title: 'Status' },
  ];

  const [selectedExportColumns, setSelectedExportColumns] = useState<string[]>(
    exportColumnDefs.map((col) => col.key)
  );
  const toggleExportColumn = (key: string) => {
    setSelectedExportColumns((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };
  const selectAllExportColumns = () => setSelectedExportColumns(exportColumnDefs.map((col) => col.key));
  const clearAllExportColumns = () => setSelectedExportColumns([]);
  const selectedExportColumnDefs = exportColumnDefs.filter((col) => selectedExportColumns.includes(col.key));

  const gridContentRef = useRef<HTMLDivElement | null>(null);
  const topScrollbarRef = useRef<HTMLDivElement | null>(null);
  const [scrollbarWidth, setScrollbarWidth] = useState<number>(3000);
  const { ui, login } = useStores();
  const { d4ur } = useServices();

  const sortPresets: { [key: string]: State; } = {
    clear: {
      sort: [
        {
          field: 'created_date',
          dir: 'asc',
        },
      ]
    },
    default: {
      filter: {
        logic: "and", filters: [
          {
            logic: "or", filters: [
              { field: "status", operator: "eq", value: "Pending" },
            ]
          }
        ]
      },
      sort: [
        {
          field: 'status',
          dir: 'asc',
        },
        {
          field: 'created_timestamp',
          dir: 'asc',
        },
      ]
    },
    week: {
      filter: {
        logic: "and", filters: [{
          logic: "and", filters: [
            { field: "check_approved", operator: "gte", value: moment().day(-5).startOf('day').toDate() }, // last friday
            { field: "check_approved", operator: "lte", value: moment().day(4).endOf('day').toDate() }, // this thursday
          ]
        }]
      },
      sort: [
        {
          field: 'created_timestamp',
          dir: 'asc',
        },
      ]
    },
    all: {
      sort: [
        {
          field: 'status',
          dir: 'asc',
        },
        {
          field: 'created_timestamp',
          dir: 'asc',
        },
      ]
    },
  }

  const [dataState, setDataState] = useState<State>(sortPresets.default);
  const [checkQueue, setCheckQueue] = useState<QueuedCheck[]>([]);
  const [selectedCheck, setSelectedCheck] = useState<QueuedCheck>();
  const [selectedCheckIds, setSelectedCheckIds] = useState<number[]>([]);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');

  type Direction = "ASC" | "DESC";
  type SortObject<T> = {
    [key in keyof T]?: Direction;
  };

  const customProcess = (data: any[], state: State) => {
    const preProcessed = process(data, state).data;
    // custom sort
    const getColumnValues = (column: any, value: any) => {
      switch (column) {
        // case 'sort_status':
        //     return value.data.value;
        default:
          return value;
      }
    }

    const sortOrder: SortObject<any> = {};

    for (const s of (state.sort || [])) {
      let fieldToSort = s.field;

      switch (fieldToSort) {
        case 'status':
          fieldToSort = 'sort_status';
          break;
      }

      sortOrder[fieldToSort] = s.dir?.toUpperCase() as Direction;
    };

    let newData = [];
    if (state.sort?.length) {
      newData = multiColumnSort(
        preProcessed,
        sortOrder,
        getColumnValues
      );
    } else {
      newData = preProcessed;
    }

    return {
      data: newData,
      total: preProcessed.length,
    } as DataResult;
  }

  const processChecks = (checks: any[]): QueuedCheck[] => {
    return checks.map((check) => {
      return new QueuedCheck(check);
    });
  };

  const [dataResult, setDataResult] = useState<DataResult>(
    customProcess(checkQueue, dataState)
  );

  const [confirmApproveVisible, setConfirmApproveVisible] =
    useState<boolean>(false);

  const [editCheckVisible, setEditCheckVisible] = useState<boolean>(false);
  const [editCheckData, setEditCheckData] = useState<{ check_queue_id: number; memo: string; check_amount: number | undefined; check_payable: string } | null>(null);
  const [editSaving, setEditSaving] = useState<boolean>(false);

  const openEditCheck = (check: QueuedCheck) => {
    setEditCheckData({
      check_queue_id: check.check_queue_id,
      memo: normalizeMemo(check.memo) ?? '',
      check_amount: check.check_amount,
      check_payable: check.check_payable || '',
    });
    setEditCheckVisible(true);
  };

  const saveEditCheck = () => {
    if (!editCheckData) return;
    setEditSaving(true);
    const normalizedMemo = normalizeMemo(editCheckData.memo);
    d4ur.updateCheckFields(
      editCheckData.check_queue_id,
      normalizedMemo,
      editCheckData.check_amount,
      editCheckData.check_payable,
    ).then(() => {
      setEditSaving(false);
      setEditCheckVisible(false);
      setEditCheckData(null);
      refreshCheckQueue();
    });
  };

  const approveCheck = (check: QueuedCheck) => {
    setSelectedCheck(check);
    setConfirmApproveVisible(true);
  };

  const doApproveCheck = () => {
    ui.setIsApprovingCheck(true);
    const finish = () => {
      ui.setIsGettingEvents(true);
      refreshCheckQueue().then(() => {
        ui.setIsGettingEvents(false);
        setConfirmApproveVisible(false);
        ui.setIsApprovingCheck(false);
      });
    }

    if (selectedCheck?.check_type === 'exception') {
      d4ur.approveCheckException(selectedCheck?.check_queue_id).then(finish);
    } else {
      d4ur.submitCheckApproval(selectedCheck?.check_queue_id).then(finish);
    }
  };

  const doApproveSelectedChecks = () => {
    if (!selectedCheckIds.length) return;
    ui.setIsApprovingCheck(true);
    setErrorMessage('');
    d4ur.submitCheckApprovalBulk(selectedCheckIds).then((ret: any) => {
      const result = ret?.result || {};
      if (ret?.response?.status >= 400) {
        setErrorMessage(result.message || 'Failed to approve selected checks.');
      } else {
        setSuccessMessage(`Approved ${result.success_count ?? selectedCheckIds.length} checks.`);
        setTimeout(() => {
          setSuccessMessage('');
        }, 2500);
      }
      refreshCheckQueue().then(() => {
        ui.setIsApprovingCheck(false);
      });
    }).catch(() => {
      setErrorMessage('Failed to approve selected checks.');
      ui.setIsApprovingCheck(false);
    });
  };

  const doDataState = (state: State) => {
    console.log('event.dataState', state);
    setDataResult(customProcess(checkQueue, state));
    setDataState(state);
  }

  const refreshCheckQueue = () => {
    return d4ur.getCheckQueue(ui.currentSeason.season_id).then((ret) => {
      const processedChecks = processChecks((ret as D4URxFetchResult).result || []);
      setCheckQueue(processedChecks);
      setDataResult(customProcess(processedChecks, dataState));
      setSelectedCheckIds([]);
      return processedChecks;
    });
  };

  useEffect(() => {
    ui.setIsGettingEvents(true);
    d4ur.getCheckQueue(ui.currentSeason.season_id).then((ret) => {
      ui.setIsGettingEvents(false);
      const processedChecks = processChecks((ret as D4URxFetchResult).result || []);
      setCheckQueue(processedChecks);
      setDataResult(customProcess(processedChecks, dataState));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (checkQueue.length === 0) return;
    const measureWidth = () => {
      const gridContent = gridContentRef.current?.querySelector('.k-grid-content') as HTMLElement;
      if (gridContent && gridContent.scrollWidth > 0) setScrollbarWidth(gridContent.scrollWidth);
    };
    const syncTopScrollbar = () => {
      const gridContent = gridContentRef.current?.querySelector('.k-grid-content') as HTMLElement;
      if (gridContent && topScrollbarRef.current) topScrollbarRef.current.scrollLeft = gridContent.scrollLeft;
    };
    requestAnimationFrame(() => {
      measureWidth();
      setTimeout(measureWidth, 200);
      const gridContent = gridContentRef.current?.querySelector('.k-grid-content') as HTMLElement;
      if (gridContent) gridContent.addEventListener('scroll', syncTopScrollbar);
    });
    window.addEventListener('resize', measureWidth);
    return () => {
      window.removeEventListener('resize', measureWidth);
      const gridContent = gridContentRef.current?.querySelector('.k-grid-content') as HTMLElement;
      if (gridContent) gridContent.removeEventListener('scroll', syncTopScrollbar);
    };
  }, [checkQueue.length, dataResult.data.length]);

  const EventActionsCell = (props: GridCellProps) => {
    const thisCheck = props.dataItem as QueuedCheck;
    if (thisCheck.status === "Approved") {
      return (
        <td>
          <p style={{ fontSize: '14px' }}>Check Approved</p>
        </td>
      );
    }
    return (
      <td>
        <Button
          className="k-button k-primary approve-btn"
          onClick={() => approveCheck(thisCheck)}
        >
          Approve Check
        </Button>
      </td>
    );
  };

  const MemoCellComponent = (props: GridCellProps) => {
    const check = props.dataItem as QueuedCheck;
    return (
      <td>
        <span style={{ whiteSpace: 'pre-wrap' }}>{normalizeMemo(check.memo) ?? ''}</span>
      </td>
    );
  };

  const EditCheckCellComponent = (props: GridCellProps) => {
    const check = props.dataItem as QueuedCheck;
    if (check.status === 'Approved' || check.check_approved) {
      return <td />;
    }
    return (
      <td>
        <button
          className="k-button k-button-md k-rounded-md"
          onClick={() => openEditCheck(check)}
        >
          Edit
        </button>
      </td>
    );
  };

  const SelectCheckCellComponent = (props: GridCellProps) => {
    const check = props.dataItem as QueuedCheck;
    if (check.status === 'Approved' || check.check_approved) {
      return <td />;
    }
    const checked = selectedCheckIds.includes(check.check_queue_id);
    return (
      <td style={{ textAlign: 'center' }}>
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => {
            const isChecked = e.target.checked;
            setSelectedCheckIds((prev) => {
              if (isChecked) {
                return prev.includes(check.check_queue_id) ? prev : [...prev, check.check_queue_id];
              }
              return prev.filter((id) => id !== check.check_queue_id);
            });
          }}
        />
      </td>
    );
  };

  const checkSentEmailCell = (props: GridCellProps) => {
    const {
      check_email_sent,
      tracking_number,
      check_approved,
      check_queue_id
    } = props.dataItem;
    if (!check_approved) {
      return (
        <td>
          <p style={{ fontSize: '14px' }}>Check not approved</p>
        </td>
      )
    } else if (!tracking_number) {
      return (
        <td>
          <p style={{ fontSize: '14px' }}>Shipping label not yet generated.</p>
        </td>
      )
    }
    const sendEmail = () => {
      return d4ur.resendCheckEmail(check_queue_id).then((ret: any) => {
        if (ret.response.status === 200) {
          setSuccessMessage('Successfully sent email!')
          setTimeout(() => {
            setSuccessMessage('')
          }, 2500)
          ui.setIsGettingEvents(true);
          refreshCheckQueue().then(() => {
            ui.setIsGettingEvents(false);
          });
        } else {
          setErrorMessage(ret.result.message || 'Error sending email.')
        }
      })
        .catch(e => console.log('TRIGGER CHECK EMAIL ERROR', e))
    }

    return (
      <td>
        <p style={{ fontSize: '14px' }}>{check_email_sent ? `Last sent at: ${moment(check_email_sent).tz('America/New_York').format('MMMM Do, h:mm a')} (EST)` : 'Tracking was added today - email will be automatically sent tomorrow. You may also send it manually now.'}</p>
        <Button
          className="k-button k-primary"
          style={{ backgroundColor: '#066FEF', color: 'white' }}
          onClick={() => { sendEmail() }}
        >
          {check_email_sent ? 'Re-Send Check Email' : 'Send Check Email'}
        </Button>
      </td>
    )
  };
  return (
    <div id="Dashboard" className="dashboard-page main-content">
      {confirmApproveVisible && (
        <Dialog
          title={"Please confirm"}
          onClose={() => {
            setConfirmApproveVisible(false);
            ui.setIsApprovingCheck(false);
          }}
        >
          <p
            style={{
              margin: "25px",
              textAlign: "center",
            }}
          >
            Approve this check?
          </p>
          <DialogActionsBar>
            <button
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
              onClick={() => setConfirmApproveVisible(false)}
            >
              Cancel
            </button>
            <button
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
              onClick={() => doApproveCheck()}
              disabled={ui.isApprovingCheck}
            >
              Yes, Approve
            </button>
          </DialogActionsBar>
        </Dialog>
      )}
      {editCheckVisible && editCheckData && (
        <Dialog title="Edit Check Fields" onClose={() => { setEditCheckVisible(false); setEditCheckData(null); }}>
          <div style={{ padding: '16px', minWidth: '340px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '4px' }}>Check Amount</label>
              <input
                type="number"
                value={editCheckData.check_amount ?? ''}
                onChange={(e) => setEditCheckData({ ...editCheckData, check_amount: e.target.value === '' ? undefined : Number(e.target.value) })}
                style={{ width: '100%', boxSizing: 'border-box', padding: '6px', fontSize: '14px' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '4px' }}>Check Payable To</label>
              <input
                type="text"
                value={editCheckData.check_payable}
                onChange={(e) => setEditCheckData({ ...editCheckData, check_payable: e.target.value })}
                style={{ width: '100%', boxSizing: 'border-box', padding: '6px', fontSize: '14px' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '4px' }}>
                Memo <span style={{ fontWeight: 'normal', fontSize: '12px', color: '#666' }}>(Leave blank for default)</span>
              </label>
              <textarea
                value={editCheckData.memo}
                onChange={(e) => setEditCheckData({ ...editCheckData, memo: e.target.value })}
                rows={3}
                style={{ width: '100%', boxSizing: 'border-box', padding: '6px', fontSize: '14px' }}
              />
            </div>
          </div>
          <DialogActionsBar>
            <button
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
              onClick={() => { setEditCheckVisible(false); setEditCheckData(null); }}
            >
              Cancel
            </button>
            <button
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
              onClick={saveEditCheck}
              disabled={editSaving}
            >
              {editSaving ? 'Saving...' : 'Save'}
            </button>
          </DialogActionsBar>
        </Dialog>
      )}
      <div className="card-container grid">
        <h3 className="card-title">
          {login.user?.credentials.display_name}:{" "}
          {localizationService.toLanguageString(
            "custom.check_approval",
            "Check Approval"
          )}
        </h3>
        <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <div className="stat-card" style={{ padding: '10px 16px', backgroundColor: '#f5f5f5', borderRadius: '5px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', minWidth: '160px' }}>
            <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#444' }}>Checks Pending Approval</h4>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Pending:</span>
              <strong>{checkQueue.filter(c => c.status === 'Pending').length}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Approved:</span>
              <strong>{checkQueue.filter(c => c.status === 'Approved').length}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Total:</span>
              <strong>{checkQueue.length}</strong>
            </div>
          </div>
        </div>
        {errorMessage ? <p style={{ color: 'red' }}>{errorMessage}</p> : null}
        {successMessage ? <p style={{ color: 'green' }}>{successMessage}</p> : null}
        <div style={{ marginBottom: '10px' }}>
          <button
            className="k-button k-button-md k-rounded-md k-button-solid"
            onClick={doApproveSelectedChecks}
            disabled={!selectedCheckIds.length || ui.isApprovingCheck}
          >
            Approve Selected Checks
          </button>
        </div>
        <div className="card-component">
          <ExcelExport data={dataResult.data} ref={_export} fileName={`checks_export_${moment().format('YYYYMMDD-hhmma')}.xlsx`}>
            {selectedExportColumnDefs.map((col) => (
              <ExcelExportColumn key={col.key} field={col.field} title={col.title} />
            ))}
          </ExcelExport>
          <div style={{ marginBottom: '12px' }}>
            <details>
              <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
                Choose Excel columns ({selectedExportColumnDefs.length} selected)
              </summary>
              <div style={{ marginTop: '8px' }}>
                <button className="k-button k-button-md k-rounded-md" onClick={selectAllExportColumns} type="button" style={{ marginRight: '8px' }}>Select All</button>
                <button className="k-button k-button-md k-rounded-md" onClick={clearAllExportColumns} type="button">Clear All</button>
              </div>
              <div style={{ marginTop: '10px', display: 'grid', gridTemplateColumns: 'repeat(3, minmax(220px, 1fr))', gap: '6px 12px' }}>
                {exportColumnDefs.map((col) => (
                  <label key={col.key} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <input type="checkbox" checked={selectedExportColumns.includes(col.key)} onChange={() => toggleExportColumn(col.key)} />
                    <span>{col.title}</span>
                  </label>
                ))}
              </div>
            </details>
          </div>

          <style>{`
            .checkapproval-grid-wrapper .k-grid-content { overflow-x: scroll; scrollbar-width: none; -ms-overflow-style: none; }
            .checkapproval-grid-wrapper .k-grid-content::-webkit-scrollbar { display: none; }
            .checkapproval-top-scrollbar { overflow-x: scroll; overflow-y: hidden; height: 14px; position: sticky; top: 0; z-index: 50; background: #f5f5f5; border-bottom: 1px solid #ddd; }
            .checkapproval-top-scrollbar::-webkit-scrollbar { height: 8px; }
            .checkapproval-top-scrollbar::-webkit-scrollbar-track { background: #f1f1f1; }
            .checkapproval-top-scrollbar::-webkit-scrollbar-thumb { background: #aaa; border-radius: 4px; }
            .checkapproval-top-scrollbar::-webkit-scrollbar-thumb:hover { background: #666; }
          `}</style>
          <div style={{ height: '70vh', overflowY: 'auto', overflowX: 'hidden' }}>
            <div ref={topScrollbarRef} className="checkapproval-top-scrollbar" onScroll={(e) => { const gc = gridContentRef.current?.querySelector('.k-grid-content') as HTMLElement; if (gc) gc.scrollLeft = e.currentTarget.scrollLeft; }}>
              <div style={{ width: scrollbarWidth, height: 1 }} />
            </div>
            <div ref={gridContentRef} className="checkapproval-grid-wrapper">
          <Grid
            data={dataResult}
            sortable={{ mode: "multiple" }}
            {...dataState}
            onDataStateChange={(event: GridDataStateChangeEvent) => {
              console.log("event.dataState", event.dataState);
              setDataResult(customProcess(checkQueue, event.dataState));
              setDataState(event.dataState);
            }}
            resizable={true}
            onColumnResize={() => { const gc = gridContentRef.current?.querySelector('.k-grid-content') as HTMLElement; if (gc) setScrollbarWidth(gc.scrollWidth); }}
          >
            <GridToolbar>
              Quick Views:
              <button className="k-button k-button-md k-rounded-md k-button-solid" onClick={() => doDataState(sortPresets.default)}>Action Needed</button>
              <button className="k-button k-button-md k-rounded-md k-button-solid" onClick={() => doDataState(sortPresets.week)}>Approved Week Fri-Thur</button>
              <button className="k-button k-button-md k-rounded-md k-button-solid" onClick={() => doDataState(sortPresets.all)}>Show All</button>
              <ToolbarSpacer />
              <button
                className="k-button k-button-md k-rounded-md k-button-solid"
                onClick={excelExport}
                disabled={selectedExportColumnDefs.length === 0}
              >
                Export ({selectedExportColumnDefs.length} columns)
              </button>
            </GridToolbar>
            <GridColumn
              field="check_type_pretty"
              title="Check Type"
              columnMenu={ColumnMenuCheckboxFilter(checkQueue, {
                searchBox: () => null,
              })}
              width={150}
            />
            <GridColumn
              field="check_amount_pretty"
              title="Check Amount"
              columnMenu={ColumnMenu}
              width={150}
            />
            <GridColumn
              field="event_name"
              title="Event Name"
              columnMenu={ColumnMenu}
              width={150}
            />
            <GridColumn
              field="dealer_name"
              title={localizationService.toLanguageString(
                "custom.dealer",
                "dealer"
              )}
              columnMenu={ColumnMenu}
              width={150}
            />
            <GridColumn
              field="ffs_event_id"
              title={"FFS Event ID"}
              columnMenu={ColumnMenu}
              width={150}
            />
            <GridColumn
              field="created_timestamp"
              title="Request Date"
              format="{0:E, MMM dd yyyy}"
              filter="date"
              columnMenu={ColumnMenu}
              width={230}
              cell={(props) => (
                <td>
                  {props.dataItem.created_timestamp
                    ? moment(props.dataItem.created_timestamp)
                        .tz('America/New_York')
                        .format('MMM DD, YYYY h:mm a z')
                    : ''}
                </td>
              )}
            />
            <GridColumn
              field="check_approved"
              title="Approved Date"
              format="{0:E, MMM dd yyyy}"
              filter="date"
              columnMenu={ColumnMenu}
              width={200}
            />
            <GridColumn
              field="check_payable"
              title="Check Payable To"
              columnMenu={ColumnMenu}
              width={200}
            />
            <GridColumn
              field="event_summary"
              title="Event Notes"
              columnMenu={ColumnMenu}
              width={280}
            />
            <GridColumn
              field="memo"
              title="Memo"
              columnMenu={ColumnMenu}
              width={260}
              cell={MemoCellComponent}
            />
            <GridColumn
              field="tracking_number"
              title={"Tracking #"}
              columnMenu={ColumnMenu}
              width={150}
            />
            <GridColumn
              field="status"
              title="Status"
              cell={EventActionsCell}
              columnMenu={ColumnMenuCheckboxFilter(checkQueue, {
                searchBox: () => null,
              })}
              width={200}
            />
            <GridColumn
              cell={checkSentEmailCell}
              field="check_email_sent"
              title="Check Email"
              columnMenu={ColumnMenuCheckboxFilter(checkQueue, {
                searchBox: () => null,
              })}
              width={200}
            />
            <GridColumn
              title="Actions"
              cell={EditCheckCellComponent}
              width={120}
            />
            <GridColumn
              title="Select"
              cell={SelectCheckCellComponent}
              width={90}
            />
          </Grid>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default observer(CheckApprovalScreen);
